/**
 * 共通フォームルートコンポーネント
 * オペレーションパッケージのタイプに応じて適切なフォームコンポーネントを表示する
 *
 * - approvalInfoがある場合: RequestTaskDetailPageContentを使用した承認フォーム
 * - DeviceInventoryパッケージ: 専用のDeviceInventoryFormContainerを使用
 * - その他のパッケージ: 標準のFormContainerを使用
 */
import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect } from 'react';
import { FullScreenOverlay, ViewportLoader } from '@/components/Loading';
import { toast } from '@/components/ui/use-toast';
import { useTranslation } from '@/lib/i18n';
import { useFormState } from '../../../-hooks/useFormState';
import { useGetSelfServiceTaskForm } from '../../../-query';
import { FormPackageContainer } from '../../../../-components/form-factory';
import { FormWithApprovalContainer } from '../../../../-components/form-factory/FormWithApprovalContainer';
import { FreeFormFormContainer } from '../../../../-components/form-factory/FreeFormFormContainer';
import { useGetSelfServiceTask } from '../../../../-query';
import { selectTaskFormVariant } from './-utils/selectTaskFormVariant';
export const Route = createFileRoute('/_individual/individual/task/$id/_operation-package/_common/form/$formId/')({
  component: RouteComponent
});
const NOT_STARTED_TIMEOUT_MS = 15_000;
function RouteComponent() {
  const {
    id: taskId,
    formId: operationTaskInstanceId
  } = Route.useParams();
  const navigate = Route.useNavigate();
  const {
    t
  } = useTranslation();

  // タスク情報からオペレーションタイプを取得
  const {
    data: taskData,
    isLoading: isTaskLoading
  } = useGetSelfServiceTask(taskId);
  // start API は非同期のため、遷移後に NotStarted の間ポーリングする
  const {
    data,
    isLoading
  } = useGetSelfServiceTaskForm(taskId, operationTaskInstanceId);
  useFormState();
  const isNotStarted = data?.status === 'NotStarted';
  useEffect(() => {
    if (!isNotStarted) return;
    const timer = setTimeout(() => {
      toast({
        description: t('individualPages.tasks.form.preparingTaskTimeout')
      });
      navigate({
        to: '/individual/task/$id',
        params: {
          id: taskId
        }
      });
    }, NOT_STARTED_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [isNotStarted, navigate, taskId, t]);

  // 入り口ごとの戻り先ポリシーが固まるまで、従来どおりタスク詳細へ固定遷移する
  const handleNavigateBack = useCallback(() => {
    void navigate({
      to: '/individual/task/$id',
      params: {
        id: taskId
      }
    });
  }, [navigate, taskId]);

  // 初回取得中のみページ全体の loader。NotStarted ポーリング中はフォームを描画したうえで
  // FullScreenOverlay（loading ダイアログ相当）を重ねる。
  if (isLoading || isTaskLoading) {
    return <ViewportLoader />;
  }
  if (!data || !taskData) {
    return null;
  }

  // approvalInfo の有無と taskType の2軸から描画 variant を判定する（純粋関数）。
  const hasApprovalInfo = 'approvalInfo' in data && data.approvalInfo != null;
  const variant = selectTaskFormVariant({
    taskType: data.taskType,
    hasApprovalInfo
  });
  const formContent = (() => {
    switch (variant) {
      case 'approval':
        return <FormWithApprovalContainer taskId={taskId} operationTaskInstanceId={operationTaskInstanceId} onNavigateBack={handleNavigateBack} formData={data} operationType={taskData.operationType} />;
      case 'freeForm':
        return <FreeFormFormContainer taskId={taskId} operationTaskInstanceId={operationTaskInstanceId} onNavigateBack={handleNavigateBack} formData={data} />;
      case 'package':
        return <FormPackageContainer operationType={taskData.operationType} taskId={taskId} operationTaskInstanceId={operationTaskInstanceId} onNavigateBack={handleNavigateBack} formData={data} />;
    }
  })();
  return <>
      {formContent}
      {isNotStarted ? <FullScreenOverlay message={t('individualPages.tasks.form.preparingTask')} /> : null}
    </>;
}